import React from "react";
import { Container, Row, Col, Button } from "react-bootstrap";
import CountUp from "react-countup";
import Pricing from "../components/Pricing";
import Typewriter from "typewriter-effect";
import Work from "../components/Work";

// images
import about from "../assets/images/business-about-img.jpg";
import ruler from "../assets/images/icons/rulers.png";
import brief from "../assets/images/icons/briefcase.png";
import present from "../assets/images/icons/presentation.png";
import tool from "../assets/images/icons/tools.png";
import test from "../assets/images/icons/browser-testing.png";
import head from "../assets/images/icons/headphones.png";

interface ServicesData {
  icon: string;
  title: string;
  description: string;
}

const servicesData: ServicesData[] = [
  { icon: present, title: "Website & Web App Development", description: "Modern, responsive, and high-performance websites built with the latest technologies — designed to convert visitors into customers." },
  { icon: test, title: "Mobile App Development", description: "We build high-quality Android & iOS apps with smooth UI, strong performance, and secure backend integration." },
  { icon: brief, title: "Custom Business Systems", description: "Tailored CRMs, dashboards, booking systems, and automation tools built specifically for your business workflow." },
  { icon: ruler, title: "UI/UX Design", description: "Clean, modern, and user-focused designs that improve user experience and strengthen your brand identity." },
  { icon: tool, title: "API & Backend Development", description: "Secure API development, payment gateways, booking engines, and third-party integrations. I build fast, scalable backends for any type of system." },
  { icon: head, title: "Support & Maintenance", description: "Ongoing support, updates, and performance optimization to keep your systems running smoothly." },
];

const Section = () => {
  return (
    <React.Fragment>
      {/* Home */}
      <section className="section business-home" id="home">
        <div className="bg-overlay"></div>
        <div className="home-center">
          <Container>
            <Row>
              <Col lg={12}>
                <div className="business-home-content text-center text-white">
                  <h4 className="intro">
                    <span className="typing">
                      <Typewriter
                        options={{
                          strings: [
                            "Full-Stack Development",
                            "Mobile App Development",
                          ],
                          autoStart: true,
                          loop: true,
                        }}
                      />
                    </span>
                  </h4>
                  <h4 className="business-home-subtitle mt-4 letter-spacing_2">
                    Hi There! Welcome to A.Rehman Web Solutions.
                  </h4>
                  <p className="business-home-desc mt-4 f-15 text-white">
                    We help businesses build scalable websites, mobile apps, and custom software systems that drive growth and automation.
                  </p>
                  <div className="mt-4 pt-3 mouse-down">
                    <Button
                      href="https://wa.me/923134561816"
                      target="_blank"
                      className="btn btn-md btn-custom"
                    >
                      Contact at Whatsapp
                    </Button>
                  </div>
                </div>
              </Col>
            </Row>
          </Container>
        </div>
      </section>

      {/* About */}
      <section className="section business-about" id="about">
        <Container>
          <Row className="align-items-center">
            <Col lg={5}>
              <div className="business-about-img position-relative">
                <img
                  src={about}
                  className="img-fluid position-relative"
                  alt=""
                />
                <div className="bg-color-overlay"></div>
              </div>
            </Col>

            <Col lg={7}>
              <div className="business-about-content ms-3 mt-5 mt-lg-0">
                <h4 className="letter-spacing_2 f-18 text-uppercase">
                  About A.Rehman Web Solutions
                </h4>

                <p className="mt-4">
                  We are a team of developers, designers, and problem-solvers focused on building high-quality digital solutions for modern businesses. We have experience working with a wide range of industries and project types, including{" "}
                  <b>business websites</b>, <b>mobile apps</b>,{" "}
                  <b>real-estate systems</b>,{" "}
                  <b>restaurant ordering platforms</b>, <b>admin panels</b>,{" "}
                  <b>CRMs</b>, and <b>custom software solutions</b>. <br /> <br /><b>Our mission is simple:</b> Help businesses become more professional, efficient, and profitable using technology.
                </p>

                <div className="mt-4">
                  <p className="business-about-features">
                    <i className="mdi mdi-check me-3 about-icon"></i>
                    <span className="text-black">
                      Fast & scalable web apps using React, Node.js, PHP & Laravel
                    </span>
                  </p>

                  <p className="business-about-features">
                    <i className="mdi mdi-check me-3 about-icon"></i>
                    <span className="text-black">Cross-platform mobile apps with React Native</span>
                  </p>

                  <p className="business-about-features">
                    <i className="mdi mdi-check me-3 about-icon"></i>
                    <span className="text-black">
                      Custom dashboards, CRMs & automation tools
                    </span>
                  </p>

                  <p className="business-about-features">
                    <i className="mdi mdi-check me-3 about-icon"></i>
                    <span className="text-black">
                      Payment systems, booking platforms & API integrations
                    </span>
                  </p>

                  <p className="business-about-features">
                    <i className="mdi mdi-check me-3 about-icon"></i>
                    <span className="text-black">
                      Complete lifecycle: Design → Development → Deployment → Support
                    </span>
                  </p>
                </div>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      <hr />

      {/* Services */}
      <section className="section business-services" id="services">
        <Container>
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <div className="text-center">
                <h2 className="heading-title-title letter-spacing_4 text-uppercase">
                  Our Services
                </h2>
                <p className="heading-title-desc text-muted mt-4">
                  We provide end-to-end development services for startups, agencies, and growing businesses looking for reliable and scalable solutions. Whether you need a simple website, a complex web application, or a cross-platform mobile app, we have the expertise to deliver high-quality results that meet your business goals.
                </p>
              </div>
            </div>
          </div>

          <Row className="mt-5 pt-3">
            {(servicesData || [])?.map(
              (service: ServicesData, index: number) => (
                <Col lg={4} key={index}>
                  <div className="text-center mt-4 p-2">
                    <div className="business-services-img mt-3">
                      <img src={service.icon} className="img-fluid" alt="" />
                    </div>
                    <h5 className="f-15 text-uppercase letter-spacing_2 mt-4 pt-3">
                      {service.title}
                    </h5>
                    <p className="text-muted mt-4 mb-0">{service.description}</p>
                  </div>
                </Col>
              )
            )}
          </Row>
        </Container>
      </section>
      <hr />

      {/* Work */}

      <Work />

      {/* Counter */}
      <section className="section business-bg-counter" id="whyus">
        <div className="bg-overlay"></div>
        <Container>
          <Row className="align-items-center">
            <Col lg={5}>
              <div className="business-counter-content text-white">
                <div className="business-counter-icon">
                  <i className="mdi mdi-account-multiple-plus-outline"></i>
                </div>
                <h1 className="line-height_1_4 mt-4">
                  A Dedicated Team Focused on Your Growth
                </h1>

                <p className="business-counter-desc mt-4 f-15 mb-0">
                  We work as your technology partner — not just a service provider. Our team ensures every project delivers real business results.
                </p>
              </div>
            </Col>

            <Col lg={6} className="offset-lg-1">
              <Row id="counter">
                <Col lg={6}>
                  <div className="business-counter-box text-center text-white">
                    <h2 className="counter-count mt-4 text-white">
                      <CountUp start={0} end={120} duration={2} />
                      <span
                        className="counter_value h2"
                        id="count1"
                      >+</span>
                    </h2>
                    <p className="letter-spacing_2 f-15 text-uppercase mt-3 mb-0">
                      Projects Completed
                    </p>
                  </div>

                  <div className="business-counter-box text-center text-white my-4">
                    <h2 className="counter-count mt-4 text-white">
                      <CountUp start={0} end={98} duration={2} />
                      <span
                        className="counter_value h2"
                        id="count2"
                      >%</span>
                    </h2>
                    <p className="letter-spacing_2 f-15 text-uppercase mt-3 mb-0">
                      Client Satisfaction
                    </p>
                  </div>
                </Col>

                <Col lg={6}>
                  <div className="business-counter-box text-center text-white">
                    <h2 className="counter-count mt-4 text-white">
                      <CountUp start={0} end={60} duration={2.5} />
                      <span
                        className="counter_value h2"
                        id="count3"
                      >+</span>
                    </h2>
                    <p className="letter-spacing_2 f-15 text-uppercase mt-3 mb-0">
                      Business Clients
                    </p>
                  </div>

                  <div className="business-counter-box text-center text-white mt-4">
                    <h2 className="counter-count mt-4 text-white">
                      <CountUp start={0} end={5} duration={2} />
                      <span
                        className="counter_value h2"
                        id="count4"
                      >+</span>
                    </h2>
                    <p className="letter-spacing_2 f-15 text-uppercase mt-3 mb-0">
                      Years of Experience
                    </p>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Pricing */}
      <Pricing />

      <hr />
    </React.Fragment>
  );
};

export default Section;
